import type { Faction } from './constants.ts';
import type { MatchPhase } from './match.ts';

/**
 * Who may say what, and who may hear it.
 *
 * The rule is the original's, and it is a good one: while the match is live you
 * can only talk to your own side, and when it is over everyone can talk to
 * everyone. It is not really a safety measure — it is what makes team chat worth
 * using. A channel everybody can read is a channel nobody plans in, and the
 * whole point of three factions is that they do not know what each other intend.
 *
 * Kept in core and expressed as a pair of predicates rather than as branching in
 * the interface, because a future mode may want general chat open throughout —
 * a duel, a warm-up lobby, a free-for-all where there are no teams to keep
 * secrets from — and when that arrives the change should be one argument rather
 * than an audit of everywhere a message is drawn.
 */

export type ChatScope = 'team' | 'all';

export interface ChatMessage {
  id: number;
  /** Empty for anything the game itself says. */
  fromId: string;
  fromName: string;
  faction: Faction;
  scope: ChatScope;
  text: string;
  /** Match clock when it was said, for ordering and for fading. */
  at: number;
}

/** Longest thing anybody may say. Beyond this it is not chat, it is a payload. */
export const CHAT_MAX = 160;

/** How many are kept. Older ones are not scrolled to; they are gone. */
export const CHAT_HISTORY = 60;

/**
 * Whether a scope is open to a player right now.
 *
 * `all` opens when the match is over — and in warm-up, when there is nothing yet
 * to give away and everybody is waiting around. It is *live* play where secrecy
 * is the point.
 */
export function canSpeak(scope: ChatScope, phase: MatchPhase): boolean {
  if (scope === 'team') return true;
  return phase !== 'live';
}

/**
 * Whether a given message reaches a given listener.
 *
 * Checked on receipt as well as on send, because the two are different moments:
 * a message sent to everybody as the match ended should still be readable a
 * second later, and a message sent to a team should never become readable just
 * because the match has since finished.
 */
export function canHear(msg: ChatMessage, listener: Faction): boolean {
  return msg.scope === 'all' || msg.faction === listener;
}

/**
 * Trims a line down to something sayable, or rejects it.
 *
 * Control characters go, because a newline in a chat line is two lines in the
 * log and an easy way to push everything else off the screen. Escaping for the
 * page is *not* done here — that belongs where the message meets the DOM, and a
 * string that arrives already escaped is a string that gets escaped twice.
 */
export function sanitiseChat(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  const text = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CHAT_MAX);

  return text.length ? text : null;
}

/**
 * How fast anybody may talk.
 *
 * Not politeness. An unlimited chat channel is an unlimited write to every
 * client in the room, which makes it the cheapest way to flood a server that
 * has otherwise been careful about how much it sends.
 */
export const CHAT_EVERY = 0.7;

export interface ChatLog {
  messages: ChatMessage[];
  nextId: number;
  /** When each player last spoke, for the rate limit. */
  lastSpoke: Map<string, number>;
}

export function makeChatLog(): ChatLog {
  return { messages: [], nextId: 1, lastSpoke: new Map() };
}

/**
 * Adds a line if the speaker is allowed to say it, and returns it if so.
 *
 * Every rule in one place, so there is exactly one door into the log and nothing
 * can be added by going round the side.
 */
export function say(
  log: ChatLog,
  speaker: { id: string; name: string; faction: Faction },
  scope: ChatScope,
  raw: unknown,
  phase: MatchPhase,
  now: number,
): ChatMessage | null {
  if (!canSpeak(scope, phase)) return null;

  const last = log.lastSpoke.get(speaker.id);
  if (last !== undefined && now - last < CHAT_EVERY) return null;

  const text = sanitiseChat(raw);
  if (!text) return null;

  const msg: ChatMessage = {
    id: log.nextId++,
    fromId: speaker.id,
    fromName: speaker.name,
    faction: speaker.faction,
    scope,
    text,
    at: now,
  };

  log.lastSpoke.set(speaker.id, now);
  log.messages.push(msg);
  if (log.messages.length > CHAT_HISTORY) log.messages.shift();
  return msg;
}
